// Time zones for analytics. A call at 23:30 in Lagos belongs to that day in
// Lagos, whoever is looking at the report. Every timestamp is turned into a
// day, hour and weekday on the clock of the person it belongs to (the
// executive or operations user), using profiles.time_zone (PHASE_20). Anyone
// without one falls back to the viewer's clock, which is how it worked before.
import { supabase, fetchAllRows } from './supabase';

export const viewerTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; } })();

const fmts = new Map();
function partsFmt(tz) {
  if (!fmts.has(tz)) {
    let f;
    try { f = new Intl.DateTimeFormat('en-GB', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23', weekday: 'short' }); }
    catch { f = new Intl.DateTimeFormat('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23', weekday: 'short' }); }
    fmts.set(tz, f);
  }
  return fmts.get(tz);
}
const DOW = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

// { ymd: 'YYYY-MM-DD', hour: 0-23, dow: 0 (Sun) - 6 } for a timestamp in `tz`.
export function partsIn(ts, tz) {
  if (!ts) return null;
  const d = ts instanceof Date ? ts : new Date(ts);
  if (isNaN(d)) return null;
  const p = {};
  partsFmt(tz || viewerTz).formatToParts(d).forEach((x) => { p[x.type] = x.value; });
  return { ymd: `${p.year}-${p.month}-${p.day}`, hour: Number(p.hour) % 24, dow: DOW[p.weekday] };
}
export const dayIn = (ts, tz) => partsIn(ts, tz)?.ymd || null;
export const todayIn = (tz) => dayIn(new Date(), tz);

// A short label such as "Lagos (GMT+1)".
export function tzLabel(tz) {
  if (!tz) return 'Not set';
  const city = tz.split('/').pop().replace(/_/g, ' ');
  try {
    const off = new Intl.DateTimeFormat('en-GB', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(new Date()).find((x) => x.type === 'timeZoneName')?.value;
    return off ? `${city} (${off})` : city;
  } catch { return city; }
}

// Every IANA zone the browser knows, for the DBA's picker.
export function allTimeZones() {
  try { if (Intl.supportedValuesOf) return Intl.supportedValuesOf('timeZone'); } catch { /* older browser */ }
  return ['Africa/Lagos', 'Africa/Accra', 'Africa/Nairobi', 'Africa/Johannesburg', 'Africa/Cairo', 'Europe/London', 'Europe/Paris', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Manila', 'Asia/Singapore', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'UTC'];
}

// id -> time zone for everyone. select('*') so it still works before PHASE_20
// (the column is simply absent and everyone falls back to the viewer's clock).
let cache = null, cachedAt = 0;
export async function loadTimeZones() {
  if (cache && Date.now() - cachedAt < 5 * 60 * 1000) return cache;
  const { data, error } = await fetchAllRows(() => supabase.from('profiles').select('*').order('id'));
  if (error) return cache || new Map();
  cache = new Map((data || []).filter((p) => p.time_zone).map((p) => [p.id, p.time_zone]));
  cachedAt = Date.now();
  return cache;
}
export function forgetTimeZones() { cache = null; }

// The time zone of one person, given the map from loadTimeZones().
export const tzFor = (map, id) => (id && map && map.get(id)) || viewerTz;

// Called once after sign-in: an executive or operations user whose time zone
// isn't recorded yet gets their device's. Never overwrites one already set
// (a DBA may have chosen it), and quietly does nothing before PHASE_20.
export async function recordOwnTimeZone(profile) {
  if (!profile || !('time_zone' in profile) || profile.time_zone) return;
  if (!['executive', 'operations'].includes(profile.role)) return;
  try { await supabase.from('profiles').update({ time_zone: viewerTz }).eq('id', profile.id); } catch { /* best effort */ }
}

// "14:05" on `tz`'s clock.
export function timeIn(ts, tz) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: tz || viewerTz }); }
  catch { return new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); }
}

// "20 Oct 2026, 14:05" on `tz`'s clock.
export function stampIn(ts, tz) {
  if (!ts) return '';
  const o = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
  try { return new Date(ts).toLocaleString('en-GB', { ...o, timeZone: tz || viewerTz }); }
  catch { return new Date(ts).toLocaleString('en-GB', o); }
}

// Adds `local` ({ ymd, hour, dow } on the caller's clock) and `stamp` to call rows.
export function localiseCalls(rows, map, idKey = 'agent_id') {
  return (rows || []).map((r) => {
    const tz = tzFor(map, r[idKey]);
    return { ...r, local: partsIn(r.started_at, tz) || { ymd: '', hour: 0, dow: 0 }, stamp: stampIn(r.started_at, tz) };
  });
}

// Minutes `tz` is ahead of UTC at `at` (default now), e.g. Lagos 60, New York -240.
export function tzOffsetMin(tz, at = new Date()) {
  try {
    const off = new Intl.DateTimeFormat('en-GB', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(at).find((x) => x.type === 'timeZoneName')?.value || 'GMT';
    const m = off.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
    return m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] || 0)) : 0;
  } catch { return 0; }
}

// "5 h ahead of", "2 h 30 min behind", "the same time as": how `a` compares with `b` now.
export function tzGap(a, b, at = new Date()) {
  const d = tzOffsetMin(a, at) - tzOffsetMin(b, at);
  if (!d) return 'the same time as';
  const h = Math.floor(Math.abs(d) / 60), m = Math.abs(d) % 60;
  return `${h ? `${h} h` : ''}${h && m ? ' ' : ''}${m ? `${m} min` : ''} ${d > 0 ? 'ahead of' : 'behind'}`;
}
